import { useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { FolderItemStatus, FolderStatus, PermissionType } from '@alucio/aws-beacon-amplify/src/models';
import type { CustomDeckORM, FolderItemORM, FolderORM } from 'src/types/orms';
import { isCustomDeckORM } from 'src/types/typeguards';
import { useDispatch, useSelector } from 'src/state/redux';
import { useAllDocumentVersionMap } from 'src/state/redux/selector/document';
import { useAllFoldersInstance } from 'src/state/redux/selector/folder';
import { useCurrentUser } from 'src/state/redux/selector/user';
import { useAppSettings } from 'src/state/context/AppSettings';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { EDITOR_TYPE, presentationBuilderActions } from 'src/state/redux/slice/PresentationBuilder/PresentationBuilder';
import type { AISearchDeckImport } from 'src/state/redux/slice/PresentationBuilder/aiImport';
import { AISearchSlideReference, resolveAISearchDeckReferences } from 'src/utils/customDeckRules/aiSearch';

export interface AISearchDeckTarget {
  folder: FolderORM;
  item: FolderItemORM;
  deck: CustomDeckORM;
}

export const getAISearchDeckTargets = (folders: FolderORM[]): AISearchDeckTarget[] => folders
  .filter(folder => folder.model.status === FolderStatus.ACTIVE)
  .flatMap(folder => folder.relations.items.flatMap(item => (
    item.model.status === FolderItemStatus.ACTIVE && isCustomDeckORM(item.relations.itemORM)
      ? [{ folder, item, deck: item.relations.itemORM }]
      : []
  )));

export const getAISearchDeckUnavailableReason = (
  { folder, deck }: AISearchDeckTarget,
  userId: string | undefined,
  isOnline: boolean,
  now = Date.now(),
): string | undefined => {
  if (!userId || folder.model.createdBy !== userId || deck.model.createdBy !== userId ||
      (folder.meta.permission && folder.meta.permission !== PermissionType.EDIT)) {
    return 'You do not have permission to edit this presentation.';
  }
  if (deck.meta.version.requiresReview) return 'Review this presentation before adding slides.';
  if (!isOnline && !deck.meta.assets.isContentCached) return 'Connect to the internet to edit this presentation.';
  const mutex = deck.model.editMutex;
  if (mutex && mutex.userId !== userId && now - new Date(mutex.lastSeenAt).getTime() < 15 * 60 * 1000) {
    return 'This presentation is being edited by another user.';
  }
  return undefined;
};

export const getNewAISearchReferences = (
  references: readonly AISearchSlideReference[],
  deck?: CustomDeckORM,
): AISearchSlideReference[] => {
  const existing = new Set(deck?.model.groups.flatMap(group => group.pages.map(page =>
    `${page.documentVersionId}:${page.pageNumber}`)));
  return references.filter(reference => !existing.has(`${reference.id}:${Number(reference.page_number)}`));
};

export const useAISearchCustomDeck = (references: readonly AISearchSlideReference[], closeModal: () => void) => {
  const dispatch = useDispatch();
  const versions = useAllDocumentVersionMap();
  const folders = useAllFoldersInstance();
  const foldersLoading = useSelector(state => !state.folder.hydrated);
  const currentUser = useCurrentUser();
  const { isOnline } = useAppSettings();
  const customDeckEnabled = useFeatureFlags('BEAC_6655_custom_deck_usage');
  const aiSearchCustomDecksEnabled = useFeatureFlags('BEAC_7936_ai_search_custom_decks');
  const enabled = customDeckEnabled && aiSearchCustomDecksEnabled;
  const userId = currentUser.userProfile?.id;
  const [selectedItemId, setSelectedItemId] = useState<string>();
  const [preparing, setPreparing] = useState(false);
  const opening = useRef(false);
  const personalFolders = useMemo(() => folders.filter(folder => folder.model.status === FolderStatus.ACTIVE &&
    folder.model.createdBy === userId), [folders, userId]);
  const targets = useMemo(() => getAISearchDeckTargets(personalFolders), [personalFolders]);
  const selected = targets.find(target => target.item.model.id === selectedItemId);
  const resolved = useMemo(() => resolveAISearchDeckReferences(references, versions), [references, versions]);
  const selectedReason = selected
    ? getAISearchDeckUnavailableReason(selected, userId, isOnline)
    : selectedItemId ? 'This presentation is no longer available.' : undefined;
  const additions = getNewAISearchReferences(resolved.references, selected?.deck);

  const areSourcesAvailable = (incoming: readonly AISearchSlideReference[]) => isOnline ||
    incoming.every(reference => versions[reference.id]?.meta.assets.isContentCached);
  const canCreate = enabled && resolved.references.length > 0 &&
    areSourcesAvailable(resolved.references) && !preparing;
  const canAdd = enabled && !foldersLoading && !!selected && !selectedReason &&
    additions.length > 0 && areSourcesAvailable(additions) && !preparing;

  const openBuilder = (existing: boolean) => {
    if (opening.current) return;
    if (!enabled) return;
    if (existing && foldersLoading) return;
    const latestResolution = resolveAISearchDeckReferences(references, versions);
    const target = existing ? targets.find(value => value.item.model.id === selectedItemId) : undefined;
    if (existing && !target) return;
    const reason = target && getAISearchDeckUnavailableReason(target, userId, isOnline);
    if (reason) return;
    const incoming = getNewAISearchReferences(latestResolution.references, target?.deck);
    if (!incoming.length || !areSourcesAvailable(incoming)) return;
    opening.current = true;
    setPreparing(true);
    const aiImport: AISearchDeckImport = { requestId: uuid(), references: incoming, source: 'AI_SEARCH' };
    closeModal();
    dispatch(target
      ? presentationBuilderActions.openPresentationBuilderEdit({
        aiImport,
        customDeckId: target.deck.model.id,
        targetFolder: target.folder,
        folderItemORM: target.item,
        editorType: EDITOR_TYPE.OWNER,
      })
      : presentationBuilderActions.openPresentationBuilderNew({ aiImport, editorType: EDITOR_TYPE.OWNER }));
  };

  return {
    folders: personalFolders,
    foldersLoading,
    targets,
    selectedItemId,
    setSelectedItemId,
    resolved,
    selectedReason,
    preparing,
    userId,
    isOnline,
    openBuilder,
    canCreate,
    canAdd,
  };
};
